import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import { activityService, UserActivity, ActivityStats } from '../services/activityService';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import Icon from '../components/common/Icon';
import { 
  HiOutlineClock, 
  HiOutlineUser, 
  HiOutlineDocumentText,
  HiOutlineTrash,
  HiOutlineRefresh
} from 'react-icons/hi';
import { 
  FiLogIn, 
  FiLogOut, 
  FiEdit, 
  FiTrash2, 
  FiEye,
  FiCalendar,
  FiFilter
} from 'react-icons/fi';
import { formatDateTimeString, getTodayISO, getPastDateISO } from '../utils/dateUtils';

const Activities: React.FC = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [activities, setActivities] = useState<UserActivity[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    action: '',
    entity_type: '',
    start_date: getPastDateISO(15),
    end_date: getTodayISO()
  });
  const hasLoadedRef = useRef(false);
  const filtersInitialized = useRef(false);

  // Load activities on mount
  useEffect(() => {
    // Prevent duplicate calls from React StrictMode
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    
    const loadData = async () => {
      await loadActivities();
      // Set after load completes to prevent filters useEffect from running on initial mount
      filtersInitialized.current = true;
    };
    loadData();
  }, []);

  // Reload activities when filters change (but not on initial mount)
  useEffect(() => {
    // Only run if filters have been initialized (after initial load completes)
    if (filtersInitialized.current) {
      loadActivities();
    }
  }, [filters]);

  const loadActivities = async () => {
    // Prevent duplicate concurrent calls
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const response = await activityService.getAll({
        ...filters,
        limit: 200
      });
      setActivities(response.data);
      setStats(response.stats);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Are you sure you want to clean up activities older than 15 days?')) {
      return;
    }

    try {
      const result = await activityService.cleanup(15);
      alert(`Cleaned up ${result.deleted_count} old activity records`);
      loadActivities();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cleanup activities');
    }
  };

  const getActionIcon = (action: string) => {
    if (action === 'login') return FiLogIn;
    if (action === 'logout') return FiLogOut;
    if (action.includes('update') || action === 'edit') return FiEdit;
    if (action.includes('delete')) return FiTrash2;
    if (action === 'page_visit') return FiEye;
    if (action === 'create') return HiOutlineDocumentText;
    return HiOutlineClock;
  };

  const getActionColor = (action: string) => {
    if (action === 'login') return 'text-green-600 bg-green-50';
    if (action === 'logout') return 'text-red-600 bg-red-50';
    if (action.includes('update') || action === 'edit') return 'text-blue-600 bg-blue-50';
    if (action.includes('delete')) return 'text-red-600 bg-red-50';
    if (action === 'page_visit') return 'text-yellow-600 bg-yellow-50';
    if (action === 'create') return 'text-indigo-600 bg-indigo-50';
    return 'text-gray-600 bg-gray-50';
  };

  const formatDateTime = (dateString: string) => formatDateTimeString(dateString);

  if (loading && activities.length === 0) {
    return (
      <Layout>
        <div className="text-center py-8">Loading activities...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Activities</h1>
            <p className="text-sm text-gray-600 mt-1">Track user actions and system events (Last 15 days)</p>
          </div>
          {(user?.role === 'org_admin' || user?.role === 'manager') && (
            <button
              onClick={handleCleanup}
              className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              <Icon component={HiOutlineTrash} className="w-5 h-5" />
              <span>Cleanup Old Records</span>
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="text-2xl font-bold text-gray-900">{stats.total_activities}</div>
              <div className="text-xs text-gray-600 mt-1">Total</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="text-2xl font-bold text-green-600">{stats.logins}</div>
              <div className="text-xs text-gray-600 mt-1">Logins</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="text-2xl font-bold text-red-600">{stats.logouts}</div>
              <div className="text-xs text-gray-600 mt-1">Logouts</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="text-2xl font-bold text-blue-600">{stats.updates}</div>
              <div className="text-xs text-gray-600 mt-1">Updates</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="text-2xl font-bold text-red-600">{stats.deletes}</div>
              <div className="text-xs text-gray-600 mt-1">Deletes</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="text-2xl font-bold text-purple-600">{stats.page_visits}</div>
              <div className="text-xs text-gray-600 mt-1">Page Visits</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <div className="text-2xl font-bold text-indigo-600">{stats.unique_users}</div>
              <div className="text-xs text-gray-600 mt-1">Users</div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center space-x-2 mb-3">
            <Icon component={FiFilter} className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Filters</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Action</label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Actions</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="page_visit">Page Visit</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Entity Type</label>
              <select
                value={filters.entity_type}
                onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Entities</option>
                <option value="customer">Customer</option>
                <option value="lead">Data</option>
                <option value="order">Order</option>
                <option value="tender">Tender</option>
                <option value="user">User</option>
                <option value="brand">Brand</option>
                <option value="page">Page</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Activities Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activities.map((activity) => (
                  <tr key={activity.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatDateTime(activity.created_at)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center">
                        <Icon component={HiOutlineUser} className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {activity.user_name || `User #${activity.user_id}`}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(activity.action)}`}>
                        <Icon component={getActionIcon(activity.action)} className="w-3 h-3 mr-1" />
                        {activity.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {activity.entity_type ? (
                        <span>
                          {activity.entity_type}
                          {activity.entity_id && ` #${activity.entity_id}`}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {activity.details || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {activity.ip_address || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {activities.length === 0 && !loading && (
            <div className="text-center py-8 text-sm text-gray-500">No activities found</div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Activities;

